import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { PaperPlaneTilt, ArrowsOut, ArrowsIn, ArrowSquareOut, SidebarSimple, Minus, ArrowsLeftRight, X } from "@phosphor-icons/react";
import { useTelegram } from "../../contexts/TelegramContext";
import { enviarChatAAparte } from "../../lib/telegramAparte";
import { leerTemaTg } from "../../lib/telegram";
import TgPanel from "./TgPanel";

// Donde NO se muestra la burbuja: solo donde el chat YA está a pantalla/módulo completo (el propio módulo
// y su pantalla aparte) -- para no duplicarlo. En todo el resto de pantallas (incluidas las demás "aparte"
// como /notas-aparte y /pantalla-llamadas, y las vistas de asistente/bilingüe/Centro de Llamadas) SÍ se muestra.
const SIN_BURBUJA = ["/chat", "/chat-aparte"];

/** Arrastrar la ventana flotante desde su barra superior (como cualquier ventana de escritorio, incluida Telegram).
 * Sin dependencias nuevas: un `mousedown` en la barra mide el punto donde se agarró, y mientras se mueve el mouse
 * la ventana pasa de posicionarse por `right/bottom` (su esquina de siempre) a `left/top` en píxeles concretos. */
function useArrastrable() {
  const [pos, setPos] = useState(null); // null = posición de siempre (abajo a la derecha); {left,top} tras arrastrar
  const estado = useRef(null);
  const onMouseDown = (e) => {
    if (e.button !== 0 || e.target.closest("button")) return; // los botones del propio título siguen funcionando
    const panel = e.currentTarget.closest(".tg-mini, .tg-ancla-panel");
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    estado.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width, h: r.height };
    const mover = (ev) => {
      if (!estado.current) return;
      const { dx, dy, w, h } = estado.current;
      const left = Math.min(Math.max(0, ev.clientX - dx), window.innerWidth - Math.min(w, 120));
      const top = Math.min(Math.max(0, ev.clientY - dy), window.innerHeight - 40);
      setPos({ left, top });
    };
    const soltar = () => { estado.current = null; window.removeEventListener("mousemove", mover); window.removeEventListener("mouseup", soltar); };
    window.addEventListener("mousemove", mover);
    window.addEventListener("mouseup", soltar);
  };
  const estilo = pos ? { left: pos.left, top: pos.top, right: "auto", bottom: "auto" } : undefined;
  return { estilo, onMouseDown };
}

/**
 * Burbuja flotante GRANDE del Chat con Telegram (lista + conversación), con la MISMA apariencia que el módulo (mismo
 * `TgPanel`/tema/componentes -- pedido del Founder, su captura real de Telegram): dos paneles lado a lado, nunca
 * apilados, tamaño cómodo por defecto, redimensionable (tirar del borde) y arrastrable desde la barra superior.
 * Título con solo iconos (minimizar / agrandar / anclar / pantalla aparte), sin texto, como la barra de una ventana.
 * «Agrandar», «Pantalla aparte» y «Anclar al lado» (panel fijo al borde derecho que ACOMODA la página en vez de taparla).
 */
export default function TgBurbuja() {
  const t = useTelegram();
  const { pathname } = useLocation();
  const oculta = SIN_BURBUJA.some((p) => pathname === p || pathname.startsWith(`${p}-`) || pathname.startsWith(`${p}/`));
  const { ui } = t;
  const anclada = ui.modo === "ancla" && !oculta;
  const [tema] = useState(leerTemaTg); // cada ventana lee la preferencia guardada; se cambia desde el módulo /chat
  const temaCl = tema === "oscuro" ? " tg-oscuro" : "";
  const arrastrePanel = useArrastrable();
  const arrastreAncla = useArrastrable();

  // el panel anclado acomoda la página: reserva su ancho a la derecha (y lo suelta al quitar el ancla o al salir)
  useEffect(() => {
    if (!anclada) return undefined;
    document.body.classList.add("tg-ancla");
    document.body.style.setProperty("--tg-ancla-w", `${ui.ancho}px`);
    return () => { document.body.classList.remove("tg-ancla"); document.body.style.removeProperty("--tg-ancla-w"); };
  }, [anclada, ui.ancho]);

  if (oculta) return null;
  const aparte = (chatId) => { enviarChatAAparte(chatId ? `${t.owner || ""}::${chatId}` : ""); if (!anclada) t.cerrarBurbuja(); };
  const bandeja = (hijos) => createPortal(<div className={`tg-root${temaCl}`}>{hijos}</div>, document.body);   // función, no componente: no remonta el panel en cada cambio

  if (anclada) {
    return bandeja(
      <>
        <aside className="tg-ancla-panel" style={{ width: ui.ancho, ...arrastreAncla.estilo }} data-testid="tg-ancla">
          <div className="tg-bh" onMouseDown={arrastreAncla.onMouseDown}>
            <PaperPlaneTilt size={16} weight="fill" color="#2AABEE" /><b>Chat · Telegram</b><span className="tg-sp" />
            <button type="button" className="tg-pillb" aria-label={ui.ancho === 400 ? "Ancho" : "Angosto"} title={ui.ancho === 400 ? "Ancho" : "Angosto"} data-testid="tg-ancho" onClick={() => t.setAncho(ui.ancho === 400 ? 560 : 400)}><ArrowsLeftRight size={15} /></button>
            <button type="button" className="tg-pillb" aria-label="Pantalla aparte" title="Pantalla aparte" data-testid="tg-aparte" onClick={() => aparte(ui.chatActivo)}><ArrowSquareOut size={15} /></button>
            <button type="button" className="tg-pillb" aria-label="Quitar ancla" title="Quitar ancla" data-testid="tg-quitar-ancla" onClick={() => t.setModo("burbuja")}><X size={15} /></button>
          </div>
          <div className="tg-bc"><TgPanel variante="ancla" activa={ui.chatActivo} onActiva={t.setChatActivo} onAparte={aparte} /></div>
        </aside>
      </>,
    );
  }

  return bandeja(
    <>
      {ui.abierto && (
        <section className={`tg-mini${ui.grande ? " grande" : ""}`} data-testid="tg-burbuja-panel" style={arrastrePanel.estilo}>
          <div className="tg-bh" onMouseDown={arrastrePanel.onMouseDown}>
            <PaperPlaneTilt size={16} weight="fill" color="#2AABEE" /><b>Chat · Telegram</b><span className="tg-sp" />
            <button type="button" className="tg-pillb" aria-label={ui.grande ? "Achicar" : "Agrandar"} title={ui.grande ? "Achicar" : "Agrandar"} data-testid="tg-agrandar" onClick={() => t.setGrande(!ui.grande)}>{ui.grande ? <ArrowsIn size={15} /> : <ArrowsOut size={15} />}</button>
            <button type="button" className="tg-pillb" aria-label="Pantalla aparte" title="Pantalla aparte" data-testid="tg-aparte" onClick={() => aparte(ui.chatActivo)}><ArrowSquareOut size={15} /></button>
            <button type="button" className="tg-pillb" aria-label="Anclar al lado" title="Anclar al lado" data-testid="tg-anclar" onClick={() => t.setModo("ancla")}><SidebarSimple size={15} /></button>
            <button type="button" className="tg-pillb" aria-label="Cerrar a burbuja" title="Minimizar" data-testid="tg-minimizar" onClick={t.cerrarBurbuja}><Minus size={15} /></button>
          </div>
          <div className="tg-bc"><TgPanel variante="burbuja" activa={ui.chatActivo} onActiva={t.setChatActivo} onAparte={aparte} /></div>
        </section>
      )}
      {!ui.abierto && t.alerta && (
        <button type="button" className="tg-toast" data-testid="tg-alerta" onClick={() => t.abrirChat(t.alerta.chat_id)}>
          <b>{t.alerta.title}</b><span>{t.alerta.text}</span><small>toque para abrir</small>
        </button>
      )}
      <button type="button" className="tg-bub" data-testid="tg-burbuja" aria-label="Abrir el chat de Telegram" onClick={() => (ui.abierto ? t.cerrarBurbuja() : t.abrirBurbuja())}>
        <PaperPlaneTilt size={26} weight="fill" color="#fff" />
        {t.unreadTotal > 0 && <span className="tg-badge" data-testid="tg-burbuja-badge">{t.unreadTotal > 99 ? "99+" : t.unreadTotal}</span>}
      </button>
    </>,
  );
}
